import LoadingFullScreen from "components/common/LoadingFullScreen";
import { useEffect, useRef } from "react";
import { Outlet, Route, Routes, useLocation } from "react-router-dom";
import { BulkScenarioUploadPage } from "./BulkScenarioUploadPage";
import { ClusterPage } from "./ClusterPage";
import { CompareScenariosPage } from "./CompareScenariosPage";
import { InQueueBreadcrumbs } from "./components/common/Breadcrumbs";
import { ClusterViewWrapper } from "./components/common/ClusterViewWrapper";
import NoSubscriptions from "./components/NoSubscriptions";
import { BULK_SCENARIO_UPLOAD_PATH } from "./components/ScenarioBuilder/scenarioBuilderParams";
import { SearchPalette } from "./components/SearchPalette/SearchPalette";
import { SearchPaletteProvider } from "./components/SearchPalette/SearchPaletteContext";
import { InQueueSidebar } from "./components/Sidebar";
import { ClusterProvider } from "./contexts/ClusterContext";
import {
    InQueueSubscriptionsProvider,
    useSubscriptions
} from "./contexts/InQueueSubscriptionsContext";
import { ProjectDataProvider } from "./contexts/ProjectDataContext";
import { useSidebarState } from "./hooks/useSidebarState";
import css from "./InQueueApp.module.scss";
import { InQueueNavActions } from "./InQueueNavActions";
import { ProjectPage } from "./ProjectPage";
import { RedirectToCluster } from "./RedirectToCluster";
import { ScenarioBuilderPage } from "./ScenarioBuilderPage";
import { ScenarioPage } from "./ScenarioPage";

export const InQueueApp: React.FC = () => {
    return (
        <InQueueSubscriptionsProvider>
            <InQueueAppBody />
        </InQueueSubscriptionsProvider>
    );
};

const InQueueAppBody: React.FC = () => {
    const subscriptions = useSubscriptions();

    if (subscriptions === "loading") {
        return <LoadingFullScreen />;
    } else if (subscriptions.length === 0) {
        return <NoSubscriptions />;
    } else {
        return (
            <Routes>
                <Route path="/" element={<RedirectToCluster />} />
                <Route element={<LayoutWithContent />}>
                    <Route path=":clusterSlug" element={<ClusterViewWrapper />}>
                        <Route index element={<ClusterPage />} />
                        <Route path=":projectId" element={<ProjectPage />} />
                        <Route
                            path=":projectId/scenario/new"
                            element={<ScenarioBuilderPage />}
                        />
                        <Route
                            path={`:projectId/scenario/${BULK_SCENARIO_UPLOAD_PATH}`}
                            element={<BulkScenarioUploadPage />}
                        />
                        <Route
                            path=":projectId/scenario/:scenarioId/edit"
                            element={<ScenarioBuilderPage />}
                        />
                        <Route
                            path=":projectId/scenario/:scenarioId"
                            element={<ScenarioPage />}
                        />
                        <Route
                            path=":projectId/compare-scenarios"
                            element={<CompareScenariosPage />}
                        />
                    </Route>
                </Route>
            </Routes>
        );
    }
};

const LayoutWithContent = () => (
    <ClusterProvider>
        <ProjectDataProvider>
            <SearchPaletteProvider>
                <LayoutBody />
            </SearchPaletteProvider>
        </ProjectDataProvider>
    </ClusterProvider>
);

const LayoutBody = () => {
    const { isSidebarOpen, openSidebar, closeSidebar } = useSidebarState();
    const appLayoutRef = useRef<HTMLDivElement | null>(null);
    const { pathname } = useLocation();
    useEffect(() => {
        if (appLayoutRef.current) {
            appLayoutRef.current.scrollTop = 0;
        }
    }, [pathname]);

    return (
        <>
            <InQueueBreadcrumbs
                isSidebarOpen={isSidebarOpen}
                onOpenSidebar={openSidebar}
            />
            <InQueueNavActions />
            <div className={css["app-wrapper"]}>
                <div className={css["app-body"]}>
                    <InQueueSidebar
                        isSidebarOpen={isSidebarOpen}
                        onOpen={openSidebar}
                        onClose={closeSidebar}
                    />
                    <SearchPalette />
                    <div className={css["app-layout"]} ref={appLayoutRef}>
                        <Outlet />
                    </div>
                </div>
            </div>
        </>
    );
};
